import { HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NbSidebarService } from '@nebular/theme';
import CryptoJS from 'crypto-js';
import { JwtPayload } from 'jwt-decode';
import { Subject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { EndPoint } from '../config/endpoint';
import { SocialLoginProviderId } from '../enum/social-login';
import { User } from '../interface/user';
import { STUDENT_MENU_ITEMS } from '../pages/pages-menu';
import { HttpService } from './http.service';
import { SessionService } from './session-service.service';
import { StorageService } from './storage.service';
import { ToasterService } from './toaster.service';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public sideBarOpen = new EventEmitter<boolean>();
  public closeDialog = new EventEmitter<boolean>();
  public dialogName = new EventEmitter<string>();
  private logoutSubject = new Subject<void>();
  public userRoleChanged = new EventEmitter<boolean>();
  public navigateToClassroomMenu = new EventEmitter<boolean>();
  public openResetPasswordModal = new EventEmitter<boolean>();
  public currentStudent = new EventEmitter<User>();
  public updateStepperValue = new EventEmitter<boolean>();
  public firstTimeUserLoaded = new EventEmitter<boolean>();
  public setSelectedCalendar = new EventEmitter<boolean>();
  public disabledSideMenu = new EventEmitter<boolean>();
  public setSelectedSchool = new EventEmitter<boolean>();
  public updateValue = new EventEmitter<boolean>();
  public studentParentTabChanged = new EventEmitter<string>();
  public studentTransferTabChanged = new EventEmitter<string>();
  public lastActiveTab = signal<string>(''); // Stores last active tab while navigating between pages
  public selectedClassroom = signal<any>(null); // Stores currently selected classroom for teacher details pages
  public evaluationOpenState = signal<boolean>(false); // Stores teacher evaluation panel open/close state
  public lastSectionIndex = signal<number>(0); // Stores last section index of evaluation page to maintain state while navigating between student details and evaluation pages
  public lastSubsectionIndex = signal<number>(0); // Stores last subsection index of evaluation page to maintain state while navigating between student details and evaluation pages
  // just managing the state
  constructor(
    private storageService: StorageService,
    private httpService: HttpService,
    private router: Router,
    private sidebarService: NbSidebarService,
    private toasterService: ToasterService,
    private sessionService: SessionService,
  ) {}

  sendEvent(): void {
    try {
      this.firstTimeUserLoaded.emit(true);
    } catch (error) {
      throw error;
    }
  }

  getLogoutSignal() {
    return this.logoutSubject.asObservable();
  }

  public isLoggedIn(): boolean {
    return this.storageService.getItem('user') ? true : false;
  }

  async checkIfUserExistWithEmailCase(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<string>(`${environment.XANO_SERVER_URL}${EndPoint.check_user_with_email_case}`, body),
      );
      return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }

  async login(body: object) {
    try {
      return await firstValueFrom(this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.Login}`, body));
    } catch (error) {
      throw error;
    }
  }

  async socialLogin(body: object) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.socialLogin}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async updateUser(id: number, body: object, isSetLocalStorage: boolean = false): Promise<any> {
    try {
      const res = await firstValueFrom(
        this.httpService.post<string>(`${environment.XANO_SERVER_URL}${EndPoint.User}/${id}`, body),
      );

      return isSetLocalStorage ? res : this.httpService.decodeLoginToken(res);
    } catch (error) {
      throw new Error('Failed to update user');
    }
  }

  async getUserTimeSession(params: { [key: string]: any }): Promise<any> {
    try {
      const httpParams = new HttpParams({ fromObject: params });
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.UserTimeSession}`, { params: httpParams }),
      );

      return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }

  async updateUserTimeSession(id: number, body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<string>(`${environment.XANO_SERVER_URL}${EndPoint.UserTimeSession}/${id}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async setUserTimeSession(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<string>(`${environment.XANO_SERVER_URL}${EndPoint.UserTimeSession}`, body),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async userById(id: number, isEncode?: boolean): Promise<JwtPayload> {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.User}/${id}`),
      );
      return isEncode ? response : this.httpService.decodeLoginToken(response);
    } catch (error) {
      throw error;
    }
  }

  async userSignUpById(id: number) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.user_sign_up}/${id}`),
      );

      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getDummyUser(role_id: number, isUser = false) {
    try {
      const response = await this.getDummyUserAsPerRole({
        role_id,
      });
      return isUser
        ? response[0]
        : {
            authToken: '',
            user: response[0],
          };
    } catch (error) {
      throw error;
    }
  }

  async getDummyUserAsPerRole(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.dummy_users}`, {
          params: body,
        }),
      );

      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async userByEmail(queryParams: { [key: string]: string }) {
    try {
      const httpParams = new HttpParams({ fromObject: queryParams });
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.user_by_email}`, { params: httpParams }),
      );

      return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getAllSignedUpUsers(params?: object) {
    try {
      const users = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.user_sign_up}`, {
          params,
        }),
      );
      return this.httpService.decodeToken(users);
    } catch (error) {
      throw error;
    }
  }

  async addSignedUpUser(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.user_sign_up}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async sendUserEmail(body) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      return await firstValueFrom(
        this.httpService.post<any>(`${environment.NODE_SERVER}/email${EndPoint.sendUserEmail}`, encryptedData),
      );
    } catch (error) {
      throw error;
    }
  }

  async addUser(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.User}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async addSSOLoginUser(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_only_user}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getParentsByEmail(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.parent_user_by_email}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getStudentByStudentCode(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.student_by_student_code}`, body),
      );

      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getEmailVerification(body: object) {
    try {
      const emailVerify = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_email_token_verification}`, body),
      );

      return this.httpService.decodeToken(emailVerify);
    } catch (error) {
      throw error;
    }
  }

  async updateUserWithPassword(id: number, body: object, isSetLocalStorage: boolean = false) {
    try {
      const res = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.update_user_and_password}/${id}`, body),
      );

      return isSetLocalStorage ? res : await this.httpService.decodeLoginToken(res);
    } catch (error) {
      throw error;
    }
  }

  async updateEmailVerification(id: number, body: object) {
    try {
      const token = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.email_token_verification}/${id}`, body),
      );
      return this.httpService.decodeTokenPagination(token);
    } catch (error) {
      throw error;
    }
  }

  async deleteTrialUserData(body: object) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.delete_trial_user_data}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  public logout(): void {
    this.logoutSubject.next();
  }

  public getRole() {
    const user = this.storageService.getItem('user');
    return user.user.role_id;
  }

  async userByRoleId(body): Promise<any[]> {
    try {
      const users = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.user_by_role_and_school_id}`, body),
      );
      return this.httpService.decodeToken(users);
    } catch (error) {
      throw error;
    }
  }

  async getUserByStudentCodes(body: object) {
    try {
      const getUserByStudentCoddes = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.user_by_student_codes}`, body),
      );
      return this.httpService.decodeToken(getUserByStudentCoddes);
    } catch (error) {
      throw error;
    }
  }

  async userByMultipleRoleAndSchoolIds(body) {
    try {
      const users = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.user_by_multiple_role_and_school_ids}`,
          body,
        ),
      );
      return this.httpService.decodeToken(users);
    } catch (error) {
      throw error;
    }
  }

  public checkIfAssessmentGoingOn(): void {
    try {
      const currentAssessmentId = this.storageService.getItem('currentOnGoingAssessment');

      if (currentAssessmentId) {
        STUDENT_MENU_ITEMS.forEach((x) => {
          x.group = true;
        });

        this.sidebarService.collapse('menu-sidebar');
        this.renderToTakeAssessment(currentAssessmentId);
      }
    } catch (error) {
      throw error;
    }
  }

  private renderToTakeAssessment(id: number): void {
    try {
      this.router.navigate(['/test-quizzes/assessment', id]);
    } catch (error) {
      throw error;
    }
  }

  async usersCountByRoleId(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.user_by_role_id}`, queryParams),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }
  async getDisableUsers(body: object) {
    try {
      const response: any = await firstValueFrom(
        this.httpService.get(`${environment.XANO_SERVER_URL}${EndPoint.getDisableusers}`, {
          params: body,
        }),
      );
      return this.httpService.decodeLoginToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getAllUsersByRoleId(body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.GetAllUsersByRoleId}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async getAllUsersBySchoolId(queryParams: object) {
    try {
      const responseToken = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.User}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeTokenPagination(responseToken);
    } catch (error) {
      throw error;
    }
  }
  async getUserByRoleIds(body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.UserByRoleIDS}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async getAllUsers(params?: object) {
    try {
      const users = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_all_users}`, {
          params,
        }),
      );
      return this.httpService.decodeToken(users);
    } catch (error) {
      throw error;
    }
  }
  async getUserByRoleIdAndSchoolId(params) {
    try {
      const users = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.get_users_by_role_id_and_school_id}`,
          params,
        ),
      );
      return this.httpService.decodeToken(users);
    } catch (error) {
      throw error;
    }
  }

  async getAllUsersCount(body: object) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.user_count_per_role_id}`,

          body,
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  async getBulkUserData(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.bulk_upload_process}`,

          queryParams,
        ),
      );
      return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }

  getKeys(role_id: number): string[] {
    try {
      switch (role_id) {
        case 3:
          return [
            'First Name',
            'Last Name',
            'Email',
            'School Issued Id',
            'Contact',
            'Address',
            'Birth Date',
            'Date of Hire',
            'Tb Clearance Date',
            'Tb Expiration Date',
            'Credential Document Number',
            'Credential Expiration Date',
            'Credential Type Employment',
            'Date of Criminal Background',
          ];
        case 8:
          return [
            'First Name',
            'Last Name',
            'Email',
            'School Issued Id',
            'Contact',
            'Address',
            'Birth Date',
            'Date of Hire',
            'Tb Clearance Date',
            'Tb Expiration Date',
            'Credential Document Number',
            'Credential Expiration Date',
            'Credential Type Employment',
            'Date of Criminal Background',
          ];
        case 9:
          return [
            'First Name',
            'Last Name',
            'Email',
            'School Issued Id',
            'Contact',
            'Address',
            'Birth Date',
            'Date of Hire',
            'Tb Clearance Date',
            'Tb Expiration Date',
            'Credential Document Number',
            'Credential Expiration Date',
            'Credential Type Employment',
            'Date of Criminal Background',
          ];
        case 10:
          return [
            'First Name',
            'Last Name',
            'Email',
            'School Issued Id',
            'Contact',
            'Address',
            'Birth Date',
            'Date of Hire',
            'Tb Clearance Date',
            'Tb Expiration Date',
            'Credential Document Number',
            'Credential Expiration Date',
            'Credential Type Employment',
            'Date of Criminal Background',
          ];
        case 12:
        case 13:
        case 14:
        case 15:
        case 16:
        case 17:
          return [
            'First Name',
            'Last Name',
            'Email',
            'School Issued Id',
            'Contact',
            'Address',
            'Birth Date',
            'Manage Users Permission',
            'Teacher View Permission',
          ];
        case 18:
          return [
            'First Name',
            'Last Name',
            'Email',
            'School Issued Id',
            'Contact',
            'Address',
            'Birth Date',
            'Grade Level',
          ];
        default:
          return [];
      }
    } catch (error) {
      throw error;
    }
  }

  async validateusers(users, databaseUsers, role_id, schoolUsers) {
    try {
      const invalidUsers = [];
      const validUsers = [];
      switch (role_id) {
        case 9:
          for (const iterator of users) {
            const isValidFirstName = iterator['First Name']
              ? iterator['First Name']?.length <= 25
                ? new RegExp("^[ A-Za-z0-9_@.#&+('Ã¡)-]{0,34}$").test(iterator['First Name'])
                : false
              : false;
            const isValidLastName = iterator['Last Name']
              ? iterator['Last Name']?.length <= 25
                ? new RegExp("^[ A-Za-z0-9_@.#&+('Ã¡)-]{0,34}$").test(iterator['Last Name'])
                : false
              : false;
            const isValidEmail = iterator['Email']
              ? new RegExp("^(?!.*\\.@)[a-zA-Z0-9._%'+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$").test(
                  iterator['Email'].trim(),
                )
                ? databaseUsers.filter((x) => x.email === iterator.Email)?.length > 0 ||
                  users.filter((user) => user.Email === iterator.Email)?.length > 0
                  ? false
                  : true
                : false
              : false;
            const isValidateIdCode = iterator['School Issued Id']
              ? iterator['School Issued Id']?.length <= 12
                ? new RegExp('[a-zA-Z0-9]*$').test(iterator['School Issued Id'])
                  ? schoolUsers.filter((x) => x.school_indentity_number === iterator['School Issued Id'])?.length > 0 ||
                    users.some(
                      (x) => x['School Issued Id'] === iterator['School Issued Id'] && iterator['Email'] !== x['Email'],
                    )
                    ? false
                    : true
                  : false
                : false
              : false;
            const isValidContact = iterator['Contact']
              ? iterator['Contact'].length === 10
                ? isNaN(Number(iterator['Contact']))
                  ? false
                  : new RegExp('^[0-9.-]*$').test(iterator['Contact'])
                : false
              : true;
            const isValidAddress = true;
            const isValidBirthDate = iterator['Birth Date']
              ? !isNaN(new Date(iterator['Birth Date']).getTime())
                ? new Date(iterator['Birth Date']).getTime() <
                  new Date(new Date().setFullYear(new Date().getFullYear() - 4)).getTime()
                  ? true
                  : false
                : false
              : true;
            const isValidDateOfCriminalBackground = iterator['Date of Criminal Background']
              ? !isNaN(new Date(iterator['Date of Criminal Background']).getTime())
                ? iterator['Birth Date']
                  ? !isNaN(new Date(iterator['Birth Date']).getTime())
                    ? new Date(iterator['Birth Date']).getTime() <
                      new Date(iterator['Date of Criminal Background']).getTime()
                    : true
                      ? true
                      : false
                  : true
                : false
              : true;
            const isValidDateOfHire = iterator['Date of Hire']
              ? !isNaN(new Date(iterator['Date of Hire']).getTime())
                ? iterator['Date of Criminal Background']
                  ? !isNaN(new Date(iterator['Date of Criminal Background']).getTime())
                    ? new Date(iterator['Date of Criminal Background']).getTime() <
                      new Date(iterator['Date of Hire']).getTime()
                    : true
                      ? true
                      : false
                  : false
                : false
              : true;
            const isValidTbClearanceDate = iterator['Tb Clearance Date']
              ? !isNaN(new Date(iterator['Tb Clearance Date']).getTime())
                ? iterator['Birth Date']
                  ? !isNaN(new Date(iterator['Birth Date']).getTime())
                    ? new Date(iterator['Birth Date']).getTime() < new Date(iterator['Tb Clearance Date']).getTime() &&
                      new Date(iterator['Birth Date']).getTime() < new Date().getTime()
                      ? true
                      : false
                    : false
                  : false
                : true
              : true;
            const isValidTbExpirationDate = iterator['Tb Expiration Date']
              ? !isNaN(new Date(iterator['Tb Expiration Date']).getTime())
                ? iterator['Tb Clearance Date']
                  ? !isNaN(new Date(iterator['Tb Clearance Date']).getTime())
                    ? new Date(iterator['Tb Expiration Date']).toDateString() ===
                      new Date(
                        new Date(iterator['Tb Clearance Date']).setFullYear(
                          new Date(iterator['Tb Clearance Date']).getFullYear() + 4,
                        ),
                      ).toDateString()
                      ? true
                      : false
                    : false
                  : false
                : false
              : true;
            const isValidCredentialDocumentNumber = iterator['Credential Document Number']
              ? iterator['Credential Document Number']?.length <= 13
                ? isNaN(Number(iterator['Credential Document Number']))
                  ? false
                  : true
                : false
              : true;
            const isValidCredentialTypeEmployement = iterator['Credential Type Employment']
              ? iterator['Credential Type Employment']?.length <= 50
                ? true
                : false
              : true;
            const isValidCredentialExpirationDate = iterator['Credential Expiration Date']
              ? !isNaN(new Date(iterator['Credential Expiration Date']).getTime())
                ? true
                : false
              : true;

            if (
              isValidFirstName &&
              isValidLastName &&
              isValidEmail &&
              isValidateIdCode &&
              isValidContact &&
              isValidAddress &&
              isValidBirthDate &&
              isValidDateOfCriminalBackground &&
              isValidDateOfHire &&
              isValidTbClearanceDate &&
              isValidTbExpirationDate &&
              isValidCredentialDocumentNumber &&
              isValidCredentialTypeEmployement &&
              isValidCredentialExpirationDate
            ) {
              validUsers.push(iterator);
            } else {
              const value = Object.keys(iterator).length;
              if (value === 14) {
                (iterator['isValidFirstName'] = isValidFirstName),
                  (iterator['isValidLastName'] = isValidLastName),
                  (iterator['isValidEmail'] = isValidEmail),
                  (iterator['isValidateIdCode'] = isValidateIdCode),
                  (iterator['isValidContact'] = isValidContact),
                  (iterator['isValidAddress'] = isValidAddress),
                  (iterator['isValidBirthDate'] = isValidBirthDate),
                  (iterator['isValidDateOfCriminalBackground'] = isValidDateOfCriminalBackground),
                  (iterator['isValidDateOfHire'] = isValidDateOfHire),
                  (iterator['isValidTbClearanceDate'] = isValidTbClearanceDate),
                  (iterator['isValidTbExpirationDate'] = isValidTbExpirationDate),
                  (iterator['isValidCredentialDocumentNumber'] = isValidCredentialDocumentNumber),
                  (iterator['isValidCredentialTypeEmployement'] = isValidCredentialTypeEmployement),
                  (iterator['isValidCredentialExpirationDate'] = isValidCredentialExpirationDate),
                  invalidUsers.push(iterator);
              }
            }
          }

          return { validUsers, invalidUsers };
        case 12:
        case 13:
        case 14:
        case 15:
        case 16:
        case 17:
          for (const iterator of users) {
            const isValidFirstName = iterator['First Name']
              ? iterator['First Name']?.length <= 25
                ? new RegExp("^[ A-Za-z0-9_@.#&+('Ã¡)-]{0,34}$").test(iterator['First Name'])
                : false
              : false;
            const isValidLastName = iterator['Last Name']
              ? iterator['Last Name']?.length <= 25
                ? new RegExp("^[ A-Za-z0-9_@.#&+('Ã¡)-]{0,34}$").test(iterator['Last Name'])
                : false
              : false;
            const isValidEmail = iterator['Email']
              ? new RegExp("^(?!.*\\.@)[a-zA-Z0-9._%'+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$").test(
                  iterator['Email'].trim(),
                )
                ? databaseUsers.filter((x) => x.email === iterator.Email)?.length > 0 ||
                  users.filter((user) => user.Email === iterator.Email)?.length > 0
                  ? false
                  : true
                : false
              : false;
            const isValidateIdCode = iterator['School Issued Id']
              ? iterator['School Issued Id']?.length <= 12
                ? new RegExp('[a-zA-Z0-9]*$').test(iterator['School Issued Id'])
                  ? schoolUsers.filter((x) => x.school_indentity_number === iterator['School Issued Id'])?.length > 0 ||
                    users.some(
                      (x) => x['School Issued Id'] === iterator['School Issued Id'] && iterator['Email'] !== x['Email'],
                    )
                    ? false
                    : true
                  : false
                : false
              : false;
            const isValidContact = iterator['Contact']
              ? iterator['Contact'].length === 10
                ? isNaN(Number(iterator['Contact']))
                  ? false
                  : new RegExp('^[0-9.-]*$').test(iterator['Contact'])
                : false
              : true;
            const isValidAddress = true;
            const isValidBirthDate = iterator['Birth Date']
              ? !isNaN(new Date(iterator['Birth Date']).getTime())
                ? new Date(iterator['Birth Date']).getTime() <
                  new Date(new Date().setFullYear(new Date().getFullYear() - 4)).getTime()
                  ? true
                  : false
                : false
              : true;
            const isValidManageUser = iterator['Manage Users Permission']
              ? iterator['Manage Users Permission']?.toLowerCase() === 'true' ||
                iterator['Manage Users Permission']?.toLowerCase() === 'false'
                ? true
                : false
              : true;
            const isValidTeacherViewAccess = iterator['Teacher View Permission']
              ? iterator['Teacher View Permission']?.toLowerCase() === 'true' ||
                iterator['Teacher View Permission']?.toLowerCase() === 'false'
                ? true
                : false
              : true;

            if (
              isValidFirstName &&
              isValidLastName &&
              isValidEmail &&
              isValidateIdCode &&
              isValidContact &&
              isValidAddress &&
              isValidBirthDate &&
              isValidManageUser &&
              isValidTeacherViewAccess
            ) {
              validUsers.push(iterator);
            } else {
              const value = Object.keys(iterator).length;
              if (value === 9) {
                (iterator['isValidFirstName'] = isValidFirstName),
                  (iterator['isValidLastName'] = isValidLastName),
                  (iterator['isValidEmail'] = isValidEmail),
                  (iterator['isValidateIdCode'] = isValidateIdCode),
                  (iterator['isValidContact'] = isValidContact),
                  (iterator['isValidAddress'] = isValidAddress),
                  (iterator['isValidBirthDate'] = isValidBirthDate),
                  (iterator['isValidManageUser'] = isValidManageUser),
                  (iterator['isValidTeacherViewAccess'] = isValidTeacherViewAccess),
                  invalidUsers.push(iterator);
              }
            }
          }

          return { validUsers, invalidUsers };
        case 18:
          for (const iterator of users) {
            const isValidFirstName = iterator['First Name']
              ? iterator['First Name']?.length <= 25
                ? new RegExp("^[ A-Za-z0-9_@.#&+('Ã¡)-]{0,34}$").test(iterator['First Name'])
                : false
              : false;
            const isValidLastName = iterator['Last Name']
              ? iterator['Last Name']?.length <= 25
                ? new RegExp("^[ A-Za-z0-9_@.#&+('Ã¡)-]{0,34}$").test(iterator['Last Name'])
                : false
              : false;
            const isValidEmail = iterator['Email']
              ? new RegExp("^(?!.*\\.@)[a-zA-Z0-9._%'+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$").test(
                  iterator['Email'].trim(),
                )
                ? databaseUsers.filter((x) => x.email === iterator.Email)?.length > 0 ||
                  users.filter(
                    (user) =>
                      user.Email === iterator.Email && user['School Issued Id'] !== iterator['School Issued Id'],
                  )?.length > 0
                  ? false
                  : true
                : false
              : false;
            const isValidateIdCode = iterator['School Issued Id']
              ? iterator['School Issued Id']?.length <= 12
                ? new RegExp('[a-zA-Z0-9]*$').test(iterator['School Issued Id'])
                  ? schoolUsers.filter((x) => x.school_indentity_number === iterator['School Issued Id'])?.length > 0 ||
                    users.some(
                      (x) => x['School Issued Id'] === iterator['School Issued Id'] && iterator['Email'] !== x['Email'],
                    )
                    ? false
                    : true
                  : false
                : false
              : false;
            const isValidContact = iterator['Contact']
              ? iterator['Contact'].length === 10
                ? isNaN(Number(iterator['Contact']))
                  ? false
                  : new RegExp('^[0-9.-]*$').test(iterator['Contact'])
                : false
              : true;
            const isValidAddress = true;
            const isValidBirthDate = iterator['Birth Date']
              ? !isNaN(new Date(iterator['Birth Date']).getTime())
                ? new Date(iterator['Birth Date']).getTime() <
                  new Date(new Date().setFullYear(new Date().getFullYear() - 4)).getTime()
                  ? true
                  : false
                : false
              : true;
            const isValidateGrade = iterator['Grade Level']
              ? isNaN(Number(iterator['Grade Level']))
                ? false
                : Number(iterator['Grade Level']) >= 0 && Number(iterator['Grade Level']) <= 12
                  ? true
                  : false
              : true;

            if (
              isValidFirstName &&
              isValidLastName &&
              isValidEmail &&
              isValidateIdCode &&
              isValidContact &&
              isValidAddress &&
              isValidBirthDate &&
              isValidateGrade
            ) {
              validUsers.push(iterator);
            } else {
              const value = Object.keys(iterator).length;
              if (value === 8) {
                (iterator['isValidFirstName'] = isValidFirstName),
                  (iterator['isValidLastName'] = isValidLastName),
                  (iterator['isValidEmail'] = isValidEmail),
                  (iterator['isValidateIdCode'] = isValidateIdCode),
                  (iterator['isValidContact'] = isValidContact),
                  (iterator['isValidAddress'] = isValidAddress),
                  (iterator['isValidBirthDate'] = isValidBirthDate),
                  (iterator['isValidateGrade'] = isValidateGrade),
                  invalidUsers.push(iterator);
              }
            }
          }

          return { validUsers, invalidUsers };

        default:
      }
    } catch (error) {
      throw error;
    }
  }

  async getAllUsersByEmailIds(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_all_users_by_email}`, body),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }
  async disabledUsersByUserId(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.disable_multiple_user}`, body),
      );
      return response;
      // return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async userByIndentityCode(body: Object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.user_by_identity_code}`, body),
      );
      return this.httpService.decodeUserToken(response);
    } catch (error) {
      // console.log(error);
    }
  }

  async deleteUser(id: number) {
    try {
      return firstValueFrom(this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.User}/${id}`));
    } catch (error) {
      throw error;
    }
  }

  async teachersBySchoolId(queryParams: any) {
    try {
      const teachers = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.TeachersBySchoolId}`, queryParams),
      );
      if (queryParams && !queryParams.page) {
        return this.httpService.decodeUserToken(teachers);
      } else {
        return this.httpService.decryptToken(teachers);
      }
      // return this.httpService.decodeToken(teachers);
    } catch (error) {
      throw error;
    }
  }
  async getPowerUpPointsByUserId(params: object) {
    try {
      const power_up_points = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.power_up_pointer}`, {
          params,
        }),
      );

      return this.httpService.decodeToken(power_up_points);
    } catch (error) {
      throw error;
    }
  }

  async updatePowerUpPoints(id: number, body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.power_up_pointer}/${id}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }
  async addPowerUpPoints(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.power_up_pointer}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async powerUpPointsByStudentIds(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.power_up_points_by_students_ids}`, body),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getUserBySchoolIdRoleIdAndEmail(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.user_by_school_id_role_id_and_email}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }
  async getUserAccessToken(params: object) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.tokens_used}`, {
          params,
        }),
      );

      // return this.httpService.decodeToken(power_up_points);
    } catch (error) {
      throw error;
    }
  }

  async addEmailVerification(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.email_token_verification}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }
  async updateUserAccessToken(body: any) {
    try {
      const response = await firstValueFrom(
        this.httpService.post(`${environment.XANO_SERVER_URL}${EndPoint.tokens_used}`, body),
      );
      return response;
      // return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }
  async checkValidUser(databaseUsers, userBody, role_id: number, schoolUsers, currentUsers, userEmail) {
    try {
      const iterator = userBody;
      let isValidFirstName = false;
      let isValidLastName = false;
      let isValidEmail = false;
      let isValidateIdCode = false;
      let isValidContact = false;
      let isValidAddress = false;
      let isValidBirthDate = false;
      switch (role_id) {
        case 9:
          isValidFirstName = iterator['First Name']
            ? iterator['First Name']?.length <= 25
              ? new RegExp("^[ A-Za-z0-9_@.#&+('Ã¡)-]{0,34}$").test(iterator['First Name'])
              : false
            : false;
          isValidLastName = iterator['Last Name']
            ? iterator['Last Name']?.length <= 25
              ? new RegExp("^[ A-Za-z0-9_@.#&+('Ã¡)-]{0,34}$").test(iterator['Last Name'])
              : false
            : false;
          isValidEmail = iterator['Email']
            ? new RegExp("^(?!.*\\.@)[a-zA-Z0-9._%'+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$").test(iterator['Email'].trim())
              ? databaseUsers.filter((x) => x.email === iterator.Email)?.length > 0 ||
                currentUsers?.validUsers.filter(
                  (validUser) =>
                    validUser.Email === iterator.Email &&
                    validUser['School Issued Id'] !== iterator['School Issued Id'],
                )?.length > 0 ||
                currentUsers?.invalidUsers.filter(
                  (invalidUser) =>
                    invalidUser.Email === iterator.Email &&
                    invalidUser['School Issued Id'] !== iterator['School Issued Id'],
                )?.length > 0
                ? false
                : true
              : false
            : false;
          isValidateIdCode = iterator['School Issued Id']
            ? iterator['School Issued Id']?.length <= 12
              ? new RegExp('[a-zA-Z0-9]*$').test(iterator['School Issued Id'])
                ? schoolUsers.filter((x) => x.school_indentity_number === iterator['School Issued Id'])?.length > 0 ||
                  currentUsers?.validUsers?.some(
                    (x) => x['School Issued Id'] === iterator['School Issued Id'] && x['Email'] !== userEmail,
                  ) ||
                  currentUsers?.invalidUsers?.some(
                    (x) => x['School Issued Id'] === iterator['School Issued Id'] && x['Email'] !== userEmail,
                  )
                  ? false
                  : true
                : false
              : false
            : false;
          isValidContact = iterator['Contact']
            ? iterator['Contact'].length === 10
              ? isNaN(Number(iterator['Contact']))
                ? false
                : new RegExp('^[0-9.-]*$').test(iterator['Contact'])
              : false
            : true;
          isValidAddress = true;
          isValidBirthDate = iterator['Birth Date']
            ? !isNaN(new Date(iterator['Birth Date']).getTime())
              ? new Date(iterator['Birth Date']).getTime() <
                new Date(new Date().setFullYear(new Date().getFullYear() - 4)).getTime()
                ? true
                : false
              : false
            : true;
          const isValidDateOfCriminalBackground = iterator['Date of Criminal Background']
            ? !isNaN(new Date(iterator['Date of Criminal Background']).getTime())
              ? iterator['Birth Date']
                ? !isNaN(new Date(iterator['Birth Date']).getTime())
                  ? new Date(iterator['Birth Date']).getTime() <
                    new Date(iterator['Date of Criminal Background']).getTime()
                  : true
                    ? true
                    : false
                : true
              : false
            : true;
          const isValidDateOfHire = iterator['Date of Hire']
            ? !isNaN(new Date(iterator['Date of Hire']).getTime())
              ? iterator['Date of Criminal Background']
                ? !isNaN(new Date(iterator['Date of Criminal Background']).getTime())
                  ? new Date(iterator['Date of Criminal Background']).getTime() <
                    new Date(iterator['Date of Hire']).getTime()
                  : true
                    ? true
                    : false
                : false
              : false
            : true;
          const isValidTbClearanceDate = iterator['Tb Clearance Date']
            ? !isNaN(new Date(iterator['Tb Clearance Date']).getTime())
              ? iterator['Birth Date']
                ? !isNaN(new Date(iterator['Birth Date']).getTime())
                  ? new Date(iterator['Birth Date']).getTime() < new Date(iterator['Tb Clearance Date']).getTime() &&
                    new Date(iterator['Birth Date']).getTime() < new Date().getTime()
                    ? true
                    : false
                  : false
                : false
              : true
            : true;
          const isValidTbExpirationDate = iterator['Tb Expiration Date']
            ? !isNaN(new Date(iterator['Tb Expiration Date']).getTime())
              ? iterator['Tb Clearance Date']
                ? !isNaN(new Date(iterator['Tb Clearance Date']).getTime())
                  ? new Date(iterator['Tb Expiration Date']).toDateString() ===
                    new Date(
                      new Date(iterator['Tb Clearance Date']).setFullYear(
                        new Date(iterator['Tb Clearance Date']).getFullYear() + 4,
                      ),
                    ).toDateString()
                    ? true
                    : false
                  : false
                : false
              : false
            : true;
          const isValidCredentialDocumentNumber = iterator['Credential Document Number']
            ? iterator['Credential Document Number']?.length <= 13
              ? isNaN(Number(iterator['Credential Document Number']))
                ? false
                : true
              : false
            : true;
          const isValidCredentialTypeEmployement = iterator['Credential Type Employment']
            ? iterator['Credential Type Employment']?.length <= 50
              ? true
              : false
            : true;
          const isValidCredentialExpirationDate = iterator['Credential Expiration Date']
            ? !isNaN(new Date(iterator['Credential Expiration Date']).getTime())
              ? true
              : false
            : true;

          if (
            isValidFirstName &&
            isValidLastName &&
            isValidEmail &&
            isValidateIdCode &&
            isValidContact &&
            isValidAddress &&
            isValidBirthDate &&
            isValidDateOfCriminalBackground &&
            isValidDateOfHire &&
            isValidTbClearanceDate &&
            isValidTbExpirationDate &&
            isValidCredentialDocumentNumber &&
            isValidCredentialTypeEmployement &&
            isValidCredentialExpirationDate
          ) {
            return { value: true };
          } else {
            return {
              value: false,
              isValidFirstName,
              isValidLastName,
              isValidEmail,
              isValidateIdCode,
              isValidContact,
              isValidAddress,
              isValidBirthDate,
              isValidDateOfCriminalBackground,
              isValidDateOfHire,
              isValidTbClearanceDate,
              isValidTbExpirationDate,
              isValidCredentialDocumentNumber,
              isValidCredentialTypeEmployement,
              isValidCredentialExpirationDate,
            };
          }
        case 12:
        case 13:
        case 14:
        case 15:
        case 16:
        case 17:
          isValidFirstName = iterator['First Name']
            ? iterator['First Name']?.length <= 25
              ? new RegExp("^[ A-Za-z0-9_@.#&+('Ã¡)-]{0,34}$").test(iterator['First Name'])
              : false
            : false;
          isValidLastName = iterator['Last Name']
            ? iterator['Last Name']?.length <= 25
              ? new RegExp("^[ A-Za-z0-9_@.#&+('Ã¡)-]{0,34}$").test(iterator['Last Name'])
              : false
            : false;
          isValidEmail = iterator['Email']
            ? new RegExp("^(?!.*\\.@)[a-zA-Z0-9._%'+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$").test(iterator['Email'].trim())
              ? databaseUsers.filter((x) => x.email === iterator.Email)?.length > 0 ||
                currentUsers?.validUsers.filter((validUser) => validUser.Email === iterator.Email)?.length > 0 ||
                currentUsers?.invalidUsers.filter((invalidUser) => invalidUser.Email === iterator.Email)?.length > 0
                ? false
                : true
              : false
            : false;
          isValidateIdCode = iterator['School Issued Id']
            ? iterator['School Issued Id']?.length <= 12
              ? new RegExp('[a-zA-Z0-9]*$').test(iterator['School Issued Id'])
                ? databaseUsers.filter((x) => x.school_indentity_number === iterator['School Issued Id'])?.length > 0 ||
                  currentUsers?.validUsers?.some(
                    (x) => x['School Issued Id'] === iterator['School Issued Id'] && x['Email'] !== userEmail,
                  ) ||
                  currentUsers?.invalidUsers?.some(
                    (x) => x['School Issued Id'] === iterator['School Issued Id'] && x['Email'] !== userEmail,
                  )
                  ? false
                  : true
                : false
              : false
            : false;
          isValidContact = iterator['Contact']
            ? iterator['Contact'].length === 10
              ? isNaN(Number(iterator['Contact']))
                ? false
                : new RegExp('^[0-9.-]*$').test(iterator['Contact'])
              : false
            : true;
          isValidAddress = true;
          isValidBirthDate = iterator['Birth Date']
            ? !isNaN(new Date(iterator['Birth Date']).getTime())
              ? new Date(iterator['Birth Date']).getTime() <
                new Date(new Date().setFullYear(new Date().getFullYear() - 4)).getTime()
                ? true
                : false
              : false
            : true;
          const isValidManageUser = iterator['Manage Users Permission']
            ? iterator['Manage Users Permission']?.toLowerCase() === 'true' ||
              iterator['Manage Users Permission']?.toLowerCase() === 'false'
              ? true
              : false
            : true;
          const isValidTeacherViewAccess = iterator['Teacher View Permission']
            ? iterator['Teacher View Permission']?.toLowerCase() === 'true' ||
              iterator['Teacher View Permission']?.toLowerCase() === 'false'
              ? true
              : false
            : true;

          if (
            isValidFirstName &&
            isValidLastName &&
            isValidEmail &&
            isValidateIdCode &&
            isValidContact &&
            isValidAddress &&
            isValidBirthDate &&
            isValidManageUser &&
            isValidTeacherViewAccess
          ) {
            return { value: true };
          } else {
            return {
              value: false,
              isValidFirstName,
              isValidLastName,
              isValidEmail,
              isValidateIdCode,
              isValidContact,
              isValidAddress,
              isValidBirthDate,
              isValidManageUser,
              isValidTeacherViewAccess,
            };
          }
        case 18:
          isValidFirstName = iterator['First Name']
            ? iterator['First Name']?.length <= 25
              ? new RegExp("^[ A-Za-z0-9_@.#&+('Ã¡)-]{0,34}$").test(iterator['First Name'])
              : false
            : false;
          isValidLastName = iterator['Last Name']
            ? iterator['Last Name']?.length <= 25
              ? new RegExp("^[ A-Za-z0-9_@.#&+('Ã¡)-]{0,34}$").test(iterator['Last Name'])
              : false
            : false;
          isValidEmail = iterator['Email']
            ? new RegExp("^(?!.*\\.@)[a-zA-Z0-9._%'+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$").test(iterator['Email'].trim())
              ? databaseUsers.filter((x) => x.email === iterator.Email)?.length > 0 ||
                currentUsers?.validUsers.filter((validUser) => validUser.Email === iterator.Email)?.length > 0 ||
                currentUsers?.invalidUsers.filter((invalidUser) => invalidUser.Email === iterator.Email)?.length > 0
                ? false
                : true
              : false
            : false;
          isValidateIdCode = iterator['School Issued Id']
            ? iterator['School Issued Id']?.length <= 12
              ? new RegExp('[a-zA-Z0-9]*$').test(iterator['School Issued Id'])
                ? databaseUsers.filter((x) => x.school_indentity_number === iterator['School Issued Id'])?.length > 0 ||
                  currentUsers?.validUsers?.some(
                    (x) => x['School Issued Id'] === iterator['School Issued Id'] && x['Email'] !== userEmail,
                  ) ||
                  currentUsers?.invalidUsers?.some(
                    (x) => x['School Issued Id'] === iterator['School Issued Id'] && x['Email'] !== userEmail,
                  )
                  ? false
                  : true
                : false
              : false
            : false;

          isValidContact = iterator['Contact']
            ? iterator['Contact'].length === 10
              ? isNaN(Number(iterator['Contact']))
                ? false
                : new RegExp('^[0-9.-]*$').test(iterator['Contact'])
              : false
            : true;
          isValidAddress = true;
          isValidBirthDate = iterator['Birth Date']
            ? !isNaN(new Date(iterator['Birth Date']).getTime())
              ? new Date(iterator['Birth Date']).getTime() <
                new Date(new Date().setFullYear(new Date().getFullYear() - 4)).getTime()
                ? true
                : false
              : false
            : true;
          const isValidateGrade = iterator['Grade Level']
            ? isNaN(Number(iterator['Grade Level']))
              ? false
              : Number(iterator['Grade Level']) >= 0 && Number(iterator['Grade Level']) <= 12
                ? true
                : false
            : true;

          if (
            isValidFirstName &&
            isValidLastName &&
            isValidEmail &&
            isValidateIdCode &&
            isValidContact &&
            isValidAddress &&
            isValidBirthDate &&
            isValidateGrade
          ) {
            return {
              value: true,
            };
          } else {
            return {
              value: false,
              isValidFirstName,
              isValidLastName,
              isValidEmail,
              isValidateIdCode,
              isValidContact,
              isValidAddress,
              isValidBirthDate,
              isValidateGrade,
            };
          }
      }
    } catch (error) {
      throw error;
    }
  }

  getUserTableSettings(role_id: number) {
    let settings = {};

    switch (role_id) {
      case 9:
        settings = {
          hideSubHeader: true,
          selectMode: 'multi',
          mode: 'external',
          actions: {
            edit: true,
            delete: true,
            position: 'right',
            add: false,
          },
          pager: {
            display: true,
            perPage: 20,
          },
          edit: {
            editButtonContent: `<img src='/assets/icons/edit-2.svg'  loading='lazy'></img>`,
          },
          delete: {
            deleteButtonContent: `<img src='/assets/icons/trash_icon.svg' loading='lazy'></img>`,
          },

          columns: {
            'First Name': {
              title: 'First Name',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidFirstName === false ? 'invalid-column' : 'valid-column'}"">${cell}
                                 <div class=${
                                   row?.isValidFirstName === false ? 'display-invalid-data' : 'd-none'
                                 }>Invalid First Name</div></span>`;
              },
            },
            'Last Name': {
              title: 'Last Name',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidLastName === false ? 'invalid-column' : 'valid-column'}"">${cell}<div class=${
                  row?.isValidLastName === false ? 'display-invalid-data' : 'd-none'
                }>Invalid Last Name</div></span>`;
              },
            },
            Email: {
              title: 'Email',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidEmail === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${
                  row?.isValidEmail === false ? 'display-invalid-data' : 'd-none'
                }>Invalid Email, Should not be present in the database and csv file</div></span>`;
              },
            },
            'School Issued Id': {
              title: 'School Issued Id',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidateIdCode === false ? 'invalid-column' : 'valid-column'}"">${cell}
                 <div class=${
                   row?.isValidateIdCode === false ? 'display-invalid-data' : 'd-none'
                 }>Invalid School Issued Id, Should not be present in the database</div>
          < /span>`;
              },
            },
            Contact: {
              title: 'Contact',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidContact === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidContact === false ? 'display-invalid-data' : 'd-none'}>Invalid Contact Number</div></>`;
              },
            },
            Address: {
              title: 'Address',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidAddress === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidAddress === false ? 'display-invalid-data' : 'd-none'}>Invalid Address</div>
    < /span>`;
              },
            },
            'Birth Date': {
              title: 'Birth Date',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidBirthDate === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${
                  row?.isValidBirthDate === false ? 'display-invalid-data' : 'd-none'
                }>Invalid Birth Date, should be atleast four years earlier</div></>`;
              },
            },
            'Date of Hire': {
              title: 'Date of Hire',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidDateOfHire === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidDateOfHire === false ? 'display-invalid-data' : 'd-none'}>Invalid Date of Hire</div></span>`;
              },
            },
            'TB Clearance Date': {
              title: 'TB Clearance Date',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidTbClearanceDate === false ? 'invalid-column' : 'valid-column'}"">${cell}<div class=${
                  row?.isValidTbClearanceDate === false ? 'display-invalid-data' : 'd-none'
                }>Invalid TB Clearance Date</div></span>`;
              },
            },
            'TB Expiration Date': {
              title: 'TB Expiration Date',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidTbExpirationDate === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${
                  row?.isValidTbExpirationDate === false ? 'display-invalid-data' : 'd-none'
                }>Invalid TB Expiration Date</div></span>`;
              },
            },
            'Credential Document Number': {
              title: 'Credential Document Number',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${
                  row?.isValidCredentialDocumentNumber === false ? 'invalid-column' : 'valid-column'
                }"">${cell}<div class=${
                  row?.isValidCredentialDocumentNumber === false ? 'display-invalid-data' : 'd-none'
                }>Invalid Document Number</div></span>`;
              },
            },
            'Credential Expiration Date': {
              title: 'Credential Expiration Date',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${
                  row?.isValidCredentialExpirationDate === false ? 'invalid-column' : 'valid-column'
                }"">${cell}<div class=${
                  row?.isValidCredentialExpirationDate === false ? 'display-invalid-data' : 'd-none'
                }>Invalid Credential Expiration Date</div></span>`;
              },
            },
            'Credential Type Employement': {
              title: 'Credential Type Employement',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidCredentialTypeEmployement === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${
                  row?.isValidCredentialTypeEmployement === false ? 'display-invalid-data' : 'd-none'
                }>Invalid Credential Type Employement</div></span>`;
              },
            },
            'Date of Criminal Background': {
              title: 'Date of Criminal Background',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidDateOfCriminalBackground === false ? 'invalid-column' : 'valid-column'}"">${cell}
                 <div class=${
                   row?.isValidDateOfCriminalBackground === false ? 'display-invalid-data' : 'd-none'
                 }>Invalid Date of Criminal Background</div>
    < /span>`;
              },
            },
          },
        };
        return settings;
      case 12:
      case 13:
      case 14:
      case 15:
      case 16:
      case 17:
        settings = {
          selectMode: 'multi',
          mode: 'external',
          actions: {
            add: false,
            edit: true,
            delete: true,
            position: 'right',
          },
          hideSubHeader: true,

          edit: {
            editButtonContent: `<img src='/assets/icons/edit-2.svg' loading='lazy'></img>`,
          },
          delete: {
            deleteButtonContent: `<img src='/assets/icons/trash_icon.svg' loading='lazy'></img>`,
          },

          pager: {
            display: true,
            perPage: 20,
          },

          columns: {
            'First Name': {
              title: 'First Name',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidFirstName === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidFirstName === false ? 'display-invalid-data' : 'd-none'}>Invalid First Name</div></>`;
              },
            },
            'Last Name': {
              title: 'Last Name',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidLastName === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidLastName === false ? 'display-invalid-data' : 'd-none'}>Invalid Last Name</div></span>`;
              },
            },
            Email: {
              title: 'Email',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidEmail === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${
                  row?.isValidEmail === false ? 'display-invalid-data' : 'd-none'
                }>Invalid Email, should not be present in the database and csv file</div></span>`;
              },
            },
            'School Issued Id': {
              title: 'School Issued Id',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidateIdCode === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${
                  row?.isValidateIdCode === false ? 'display-invalid-data' : 'd-none'
                }>Invalid School Issued Id, should not be present in the database</div></span>`;
              },
            },
            Contact: {
              title: 'Contact',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidContact === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidContact === false ? 'display-invalid-data' : 'd-none'}>Invalid Contact Number</div></span>`;
              },
            },
            Address: {
              title: 'Address',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidAddress === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidAddress === false ? 'display-invalid-data' : 'd-none'}>Invalid Address</div></span>`;
              },
            },
            'Birth Date': {
              title: 'Birth Date',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidBirthDate === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${
                  row?.isValidBirthDate === false ? 'display-invalid-data' : 'd-none'
                }>Invalid Birth Date, should be atleast four years earlier</div></span>`;
              },
            },
            'Manage Users Permission': {
              title: 'Manage Users Permission',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidManageUser === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidManageUser === false ? 'display-invalid-data' : 'd-none'}>Invalid Manage User Value</div></span>`;
              },
            },
            'Teacher View Permission': {
              title: 'Teacher View Permission',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidTeacherViewAccess === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${
                  row?.isValidTeacherViewAccess === false ? 'display-invalid-data' : 'd-none'
                }>Invalid Teacher View Access Value</div></span>`;
              },
            },
          },
        };
        return settings;
      case 18:
        settings = {
          selectMode: 'multi',
          mode: 'external',
          hideSubHeader: true,
          actions: {
            edit: true,
            delete: true,
            position: 'right',
            add: false,
          },
          pager: {
            display: true,
            perPage: 20,
          },
          edit: {
            editButtonContent: `<img src='/assets/icons/edit-2.svg'  loading='lazy'/></img>`,
          },
          delete: {
            deleteButtonContent: `<img src='/assets/icons/trash_icon.svg' loading='lazy'/></img>`,
          },

          columns: {
            'First Name': {
              title: 'First Name',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidFirstName === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidFirstName === false ? 'display-invalid-data' : 'd-none'}>Invalid First Name</div></span>`;
              },
            },
            'Last Name': {
              title: 'Last Name',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidLastName === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidLastName === false ? 'display-invalid-data' : 'd-none'}>Invalid Last Name</div></span>`;
              },
            },
            Email: {
              title: 'Email',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidEmail === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${
                  row?.isValidEmail === false ? 'display-invalid-data' : 'd-none'
                }>Invalid Email, should not be present in the database and csv file</div></span>`;
              },
            },
            'School Issued Id': {
              title: 'School Issued Id',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidateIdCode === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${
                  row?.isValidateIdCode === false ? 'display-invalid-data' : 'd-none'
                }>Invalid School Issued Id, should not be present in the database</div></span>`;
              },
            },
            Contact: {
              title: 'Contact',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidContact === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidContact === false ? 'display-invalid-data' : 'd-none'}>Invalid Contact Number</div></span>`;
              },
            },
            Address: {
              title: 'Address',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidAddress === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidAddress === false ? 'display-invalid-data' : 'd-none'}>Invalid Address</div></span>`;
              },
            },
            'Birth Date': {
              title: 'Birth Date',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidBirthDate === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${
                  row?.isValidBirthDate === false ? 'display-invalid-data' : 'd-none'
                }>Invalid Birth Date, should be atleast four years earlier</div></span>`;
              },
            },
            'Grade Level': {
              title: 'Grade Level',
              type: 'html',
              valuePrepareFunction: (cell, row) => {
                return `<span class="${row?.isValidateGrade === false ? 'invalid-column' : 'valid-column'}"">${cell}
                <div class=${row?.isValidateGrade === false ? 'display-invalid-data' : 'd-none'}>Invalid Grade Level</div></span>`;
              },
            },
          },
        };
        return settings;
      default:
    }
  }

  getProfileData(userValues, role_id) {
    try {
      switch (role_id) {
        case 9:
          return {
            dateOfHire:
              userValues['Date of Hire'] && !isNaN(new Date(userValues['Date of Hire']).getTime())
                ? new Date(userValues['Date of Hire']).setDate(new Date(userValues['Date of Hire']).getDate() + 1)
                : null,
            tbClearanceDate:
              userValues['Tb Clearance Date'] && !isNaN(new Date(userValues['Tb Clearance Date']).getTime())
                ? new Date(userValues['Tb Clearance Date']).setDate(
                    new Date(userValues['Tb Clearance Date']).getDate() + 1,
                  )
                : null,
            tbExpirationDate:
              userValues['Tb Expiration Date'] && !isNaN(new Date(userValues['Tb Expiration Date']).getTime())
                ? new Date(userValues['Tb Expiration Date']).setDate(
                    new Date(userValues['Tb Expiration Date']).getDate() + 1,
                  )
                : null,
            credentialDocumentNumber: userValues['Credential Document Number']
              ? isNaN(userValues['Credential Document Number'])
                ? ''
                : Number(userValues['Credential Document Number'])
              : null,
            credentialExpirationDate:
              userValues['Credential Expiration Date'] &&
              !isNaN(new Date(userValues['Credential Expiration Date']).getTime())
                ? new Date(userValues['Credential Expiration Date']).setDate(
                    new Date(userValues['Credential Expiration Date']).getDate() + 1,
                  )
                : null,
            credentialTypeEmployment: userValues['Credential Type Employment']
              ? userValues['Credential Type Employment']
              : '',
            dateOfCriminalBackground:
              userValues['Date of Criminal Background'] &&
              !isNaN(new Date(userValues['Date of Criminal Background']).getTime())
                ? new Date(userValues['Date of Criminal Background']).setDate(
                    new Date(userValues['Date of Criminal Background']).getDate() + 1,
                  )
                : null,
          };
        case 12:
        case 13:
        case 14:
        case 15:
        case 16:
        case 17:
          return {
            isManageUsers:
              userValues['Manage Users Permission'] && userValues['Manage Users Permission']?.toLowerCase() === 'true'
                ? true
                : false,
            isTeacherView:
              userValues['Teacher View Permission'] && userValues['Teacher View Permission']?.toLowerCase() === 'true'
                ? true
                : false,
          };
        case 18:
          return {
            grades:
              userValues['Grade Level'] && userValues['Grade Level'] > 0 ? Number(userValues['Grade Level']) + 2 : null,
          };
        default:
      }
    } catch (error) {
      return {};
    }
  }

  removeAdminDashboardObservers() {
    try {
      for (const [index] of this.dialogName.observers.entries()) {
        if (index !== this.dialogName.observers.length - 1) {
          this.dialogName.observers[index]['isStopped'] = true;
          this.dialogName.observers[index]['closed'] = true;

          this.dialogName.observers.splice(index, 1);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteBulkUserProcess(queryParams: object) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.bulk_upload_process_delete}`,

          queryParams,
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  async deletePowerUpPointer(id: number) {
    try {
      return firstValueFrom(
        this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.power_up_pointer}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }

  async messagesUsersBySchoolId(body) {
    try {
      const message = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.messages_users_by_last_message_id}`, body),
      );
      return this.httpService.decodeToken(message);
    } catch (error) {
      throw error;
    }
  }

  async addMultipleUsers(body) {
    try {
      const addMultipleUser: any = await firstValueFrom(
        this.httpService.post(`${environment.XANO_SERVER_URL}${EndPoint.add_multiple_users}`, body),
      );
      return this.httpService.decodeToken(addMultipleUser);
    } catch (error) {
      throw error;
    }
  }

  async parentsMessagesUser(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.parents_messages_users}`, body),
      );
      return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }
  async parentOfStudent(params: Object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.parent_messages_student}`, {
          params,
        }),
      );
      return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }

  async addMultipleStudents(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.add_multiple_students}`, body),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getFeedBack(params: Object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.Feedback}`, {
          params,
        }),
      );
      return this.httpService.decodeTokenPagination(response);
    } catch (error) {
      throw error;
    }
  }

  async updateFeedback(id: number, body: Object) {
    try {
      const token = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.Feedback}/${id}`, body),
      );
      return this.httpService.decodeTokenPagination(token);
    } catch (error) {
      throw error;
    }
  }

  async addOrEditUserActivityLog(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.put<any>(`${environment.XANO_SERVER_URL}${EndPoint.user_activity_log}`, body),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async updateActivityStatus(body: object) {
    try {
      return await firstValueFrom(
        this.httpService.put<any>(`${environment.XANO_SERVER_URL}${EndPoint.activity_status}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async getTeachersBySchoolId(queryParams: any) {
    try {
      const teachers = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_teachers_by_school_id}`, queryParams),
      );
      if (queryParams && !queryParams.page) {
        return this.httpService.decodeUserToken(teachers);
      } else {
        return this.httpService.decryptToken(teachers);
      }
      // return this.httpService.decodeToken(teachers);
    } catch (error) {
      throw error;
    }
  }

  async checkIfUserExistWithEmailCaseAndUserId(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<string>(
          `${environment.XANO_SERVER_URL}${EndPoint.check_user_with_email_case_and_uid}`,
          body,
        ),
      );
      return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }

  async mergeTeacherOnlyData(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<string>(
          `${environment.XANO_SERVER_URL}${EndPoint.merge_teacher_only_data_with_real_school}`,
          body,
        ),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // function to add document record to
  async addDocumentData(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<string>(`${environment.XANO_SERVER_URL}${EndPoint.store_credential_data}`, body),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // function to get document record by user id
  async getDocumentsDataByUserId(id: number): Promise<JwtPayload> {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.store_credential_data}/${id}`),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // update the document record by Id
  async updateDocumentDataById(id: number, body: object): Promise<any> {
    try {
      const response = await firstValueFrom(
        this.httpService.post<string>(`${environment.XANO_SERVER_URL}${EndPoint.store_credential_data}/${id}`, body),
      );

      return response;
    } catch (error) {
      throw new Error('Failed to update user');
    }
  }

  async addLcapReportsData(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<string>(`${environment.XANO_SERVER_URL}${EndPoint.lcap_reports_data}`, body),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // function to get document record by user id
  async getLcapReportsData(id: number): Promise<JwtPayload> {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.lcap_reports_data}/${id}`),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
  async getSignupActivity(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<string>(`${environment.XANO_SERVER_URL}${EndPoint.signup_activity}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  checkSSOAccess(user: any): boolean {
    if (
      user.provider_id !== SocialLoginProviderId.GOOGLE_ID &&
      user.provider_id !== SocialLoginProviderId.APPLE_ID &&
      user.provider_id !== SocialLoginProviderId.MICROSOFT_ID
    ) {
      this.toasterService.showToast('You signed up using email and password. Please use that to login.', 'danger');
      return false;
    }
    return true;
  }

  checkPortalAccess(user: any): boolean {
    if (
      user.provider_id === SocialLoginProviderId.GOOGLE_ID ||
      user.provider_id === SocialLoginProviderId.APPLE_ID ||
      user.provider_id === SocialLoginProviderId.MICROSOFT_ID
    ) {
      this.toasterService.showToast('You signed up using SSO. Please use that to login.', 'danger');
      return false;
    }
    return true;
  }
}
